import { Box, Stack, Chip, Typography, Button } from '@mui/material';
import { useShow } from '@refinedev/core';
import { Show } from '@refinedev/mui';
import { genDocumentSlug } from 'shiver-core/lib';
import { DOCUMENT_TYPES, REGIONS, type DocumentTypeKey } from 'shiver-core/static';
import { AuthorizedImage } from '../../../components/AuthorizedImage.tsx';
import { ReadOnlyField } from '../../../components/fields/ReadOnlyField.tsx';
import { getAppBaseUrl } from '../../../helpers/appUrl.ts';
import { DefaultDocument } from '../../../types/index.ts';
import { AttachmentList } from './AttachmentListField.tsx';

export const DocumentShow = () => {
  const {
    query: { data, isLoading },
  } = useShow<DefaultDocument>({
    resource: 'contents/documents',
    queryOptions: {
      select({ data }) {
        return {
          data: DefaultDocument.parse(data),
        };
      },
    },
  });

  const record = data?.data;

  const isPublished = record?.publishedAt && record.publishedAt <= new Date();

  // プレビューURLの生成
  const previewUrl = record
    ? `${getAppBaseUrl()}/document/${genDocumentSlug(record.id)}`
    : undefined;

  return (
    <Show
      isLoading={isLoading}
      headerButtons={({ defaultButtons }) => (
        <>
          {previewUrl && (
            <Button
              variant="contained"
              color="primary"
              href={previewUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ textDecoration: 'none' }}
            >
              プレビュー
            </Button>
          )}
          {defaultButtons}
        </>
      )}
    >
      <Stack spacing={2}>
        <Box display="flex" gap={2}>
          <ReadOnlyField label="公開日時" sx={{ flex: 1 }}>
            {record?.publishedAt ? record.publishedAt.toLocaleString('ja-JP') : '未公開'}
          </ReadOnlyField>
          <ReadOnlyField label="記事タイプ" sx={{ flex: 1 }}>
            {record?.type ? DOCUMENT_TYPES[record.type as DocumentTypeKey]?.name : '未設定'}
          </ReadOnlyField>
          <ReadOnlyField label="国・地域" sx={{ flex: 3 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {record?.regions?.map((region, index) => (
                <Chip key={index} label={REGIONS[region]?.name} size="small" />
              ))}
            </Box>
          </ReadOnlyField>
        </Box>
        <ReadOnlyField label="タイトル" fullWidth>
          {record?.title}
        </ReadOnlyField>
        <ReadOnlyField label="リード文" fullWidth>
          {record?.lede}
        </ReadOnlyField>
        <ReadOnlyField label="本文" fullWidth>
          <Box dangerouslySetInnerHTML={{ __html: record?.textHtml ?? '' }} />
        </ReadOnlyField>
        <ReadOnlyField label="執筆者" fullWidth>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {record?.authors?.map((item, index) => (
              <Chip key={index} label={`${item.name}（${item.jobTitle}）`} size="small" />
            ))}
          </Box>
        </ReadOnlyField>
        {record?.featuredImageKey && (
          <ReadOnlyField label="サムネイル画像" fullWidth>
            <AuthorizedImage
              src={`/.api/v1/contents/media/${record.featuredImageKey}`}
              alt="サムネイル"
              style={{
                maxWidth: '100%',
                height: 'auto',
                aspectRatio: '16/9',
                objectFit: 'cover',
                border: '1px solid rgba(0, 0, 0, 0.23)',
                borderRadius: 1,
              }}
            />
          </ReadOnlyField>
        )}
        <ReadOnlyField label="関連資料" sx={{ mb: 2 }}>
          <AttachmentList items={record?.attachments ?? []} />
        </ReadOnlyField>
        <ReadOnlyField label="カテゴリ／分野" fullWidth>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {record?.sectors?.map((item, index) => (
              <Chip key={index} label={`${item.category}／${item.name}`} size="small" />
            ))}
          </Box>
        </ReadOnlyField>
        <ReadOnlyField label="トピック" fullWidth>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {record?.topics?.map((item, index) => (
              <Chip key={index} label={item.name} size="small" />
            ))}
          </Box>
        </ReadOnlyField>
        <ReadOnlyField label="シリーズ" fullWidth>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {record?.series?.map((item, index) => (
              <Chip key={index} label={item.name} size="small" />
            ))}
          </Box>
        </ReadOnlyField>
        <ReadOnlyField label="タイムライン" fullWidth>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {record?.timelines?.map((item, index) => (
              <Chip key={index} label={item.name} size="small" />
            ))}
          </Box>
        </ReadOnlyField>
        <Box display="flex" gap={2}>
          <ReadOnlyField label="注目フラグ（タイムライン）" fullWidth>
            <Typography variant="body2" color={record?.isFeatured ? 'textPrimary' : 'textDisabled'}>
              {record?.isFeatured ? 'ON' : 'OFF'}
            </Typography>
          </ReadOnlyField>
          <ReadOnlyField label="更新日時" sx={{ flex: 1 }}>
            {record?.updatedAt ? record.updatedAt.toLocaleString('ja-JP') : '未公開'}
          </ReadOnlyField>
          <ReadOnlyField label="公開設定" fullWidth>
            <Typography variant="body2" color={isPublished ? 'textPrimary' : 'textDisabled'}>
              {isPublished ? '公開' : '非公開'}
            </Typography>
          </ReadOnlyField>
          <ReadOnlyField label="閲覧制限" fullWidth>
            <Typography variant="body2" color={record?.isSpecialOpen ? 'warning' : 'info'}>
              {record?.isSpecialOpen ? '特別一般公開' : '会員向け'}
            </Typography>
          </ReadOnlyField>
        </Box>
        <Box display="flex" gap={2}>
          <ReadOnlyField label="作成日時" sx={{ flex: 1 }}>
            {record?.createdAt?.toLocaleString('ja-JP') ?? '-'}
          </ReadOnlyField>
          <ReadOnlyField label="作成者" sx={{ flex: 1 }}>
            {record?.createdBy}
          </ReadOnlyField>
          <ReadOnlyField label="変更日時" sx={{ flex: 1 }}>
            {record?.modifiedAt?.toLocaleString('ja-JP') ?? '-'}
          </ReadOnlyField>
          <ReadOnlyField label="変更者" sx={{ flex: 1 }}>
            {record?.modifiedBy}
          </ReadOnlyField>
        </Box>
        <ReadOnlyField label="関連記事" fullWidth>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {record?.relatedDocuments?.map((item, index) => (
              <Chip key={index} label={item.title} size="small" />
            ))}
          </Box>
        </ReadOnlyField>
      </Stack>
    </Show>
  );
};
